import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.tsx';
import { userService, User } from '../services/userService.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import StackCell from '../components/ui/StackCell.tsx';
import OpenAction from '../components/ui/OpenAction.tsx';
import Avatar from '../components/Avatar.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<User | null>(null);
  const [toggling, setToggling] = useState(false);

  const { data: users = [], isLoading, error } = useQuery('users', userService.getAllUsers);

  const activateUserMutation = useMutation(userService.activateUser, {
    onSuccess: () => queryClient.invalidateQueries('users'),
  });
  const deactivateUserMutation = useMutation(userService.deactivateUser, {
    onSuccess: () => queryClient.invalidateQueries('users'),
  });

  const filtered = useMemo(() => {
    return (users || []).filter((u: User) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q);
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, roleFilter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error) return <div className="field-error">Erro ao carregar usuários.</div>;

  return (
    <div>
      <PageHeader
        breadcrumb="Config / Usuários"
        title="Usuários"
        subtitle="Permissões e status"
        actions={
          hasPermission('users.create') ? (
            <button type="button" className="btn btn-primary" onClick={() => navigate('/users/new')}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Convidar
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Papel</span>
            <select className="select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
              <option value="all">Todos</option>
              <option value="admin">Admin</option>
              <option value="user">Usuário</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Nome ou e-mail"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Usuários</div>
          <div className="kpi-value">{filtered.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Admins</div>
          <div className="kpi-value">{(users || []).filter(u => u.role === 'admin').length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Ativos</div>
          <div className="kpi-value">{(users || []).filter(u => u.active).length}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Usuários</div>
          <span className="muted">{filtered.length} registros</span>
        </div>
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Nome</th>
                <th className="hide-m">E-mail</th>
                <th className="hide-m">Papel</th>
                <th className="hide-m">Status</th>
                <th className="hide-m">MFA</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u: User) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={u.name} size="sm" src={u.avatar} />
                      <StackCell
                        title={u.name}
                        meta={u.email}
                        stackExtra={
                          <>
                            {u.role === 'admin' ? 'Admin' : 'Usuário'} · {u.active ? 'Ativo' : 'Inativo'} · MFA {u.mfaEnabled ? 'Ativo' : 'Inativo'}
                          </>
                        }
                      />
                    </div>
                  </td>
                  <td className="hide-m">{u.email}</td>
                  <td className="hide-m">
                    <StatusChip label={u.role === 'admin' ? 'Admin' : 'Usuário'} tone={u.role === 'admin' ? 'blue' : 'slate'} />
                  </td>
                  <td className="hide-m">
                    <StatusChip label={u.active ? 'Ativo' : 'Inativo'} tone={u.active ? 'green' : 'slate'} />
                  </td>
                  <td className="hide-m">
                    <StatusChip label={u.mfaEnabled ? 'Ativo' : 'Inativo'} tone={u.mfaEnabled ? 'green' : 'slate'} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="row-actions">
                      <OpenAction onClick={() => navigate(`/users/${u.id}`)} />
                      {hasPermission('users.update') && (
                        <button
                          type="button"
                          className="link-btn action-keep"
                          onClick={() => setToggleTarget(u)}
                        >
                          {u.active ? 'Desativar' : 'Ativar'}
                        </button>
                      )}
                      {hasPermission('users.delete') && (
                        <button
                          type="button"
                          className="link-btn action-keep action-danger"
                          onClick={() => { setDeleteId(u.id); setShowDeleteModal(true); }}
                        >
                          Excluir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir usuário"
        message="Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            await userService.deleteUser(deleteId);
            queryClient.invalidateQueries('users');
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
      />
      <ConfirmationModal
        isOpen={toggleTarget != null}
        onClose={() => { if (!toggling) setToggleTarget(null); }}
        title={toggleTarget?.active ? 'Desativar usuário' : 'Ativar usuário'}
        message={
          toggleTarget?.active
            ? `Desativar ${toggleTarget.name}? A conta fica inativa e o login deixa de funcionar.`
            : `Ativar ${toggleTarget?.name || 'este usuário'}? A conta volta a poder entrar.`
        }
        confirmText={toggleTarget?.active ? 'Desativar' : 'Ativar'}
        cancelText="Cancelar"
        type={toggleTarget?.active ? 'warning' : 'info'}
        isLoading={toggling}
        onConfirm={async () => {
          if (!toggleTarget) return;
          setToggling(true);
          try {
            if (toggleTarget.active) {
              await deactivateUserMutation.mutateAsync(toggleTarget.id);
            } else {
              await activateUserMutation.mutateAsync(toggleTarget.id);
            }
          } finally {
            setToggling(false);
            setToggleTarget(null);
          }
        }}
      />
    </div>
  );
};

export default UserManagement;
